<template>
  <div
    v-if="available && (!editing || hasTranslation || job)"
    class="record-translation mt-3"
  >
    <v-checkbox
      v-if="!saved"
      :input-value="value"
      :disabled="disabled || busy || (job && job.status === 'review')"
      hide-details
      class="mt-0"
      :label="$t(editing ? 'Translate again into Spanish · 1 credit' : 'Translate into Spanish · 1 credit')"
      @change="$emit('input', $event)"
    />
    <div
      v-if="busy"
      role="status"
      class="text-caption mt-2"
    >
      {{ $t('Your memory is saved. Translating into Spanish…') }}
    </div>
    <v-alert
      v-if="message"
      dense
      outlined
      :type="failed || (job && job.status === 'review') ? 'warning' : 'success'"
      class="mt-2"
      role="status"
    >
      {{ message }}
      <v-btn
        v-if="failed"
        small
        text
        :disabled="busy"
        @click="translate({ failedJobId: job && job.jobId })"
      >
        {{ $t('Retry translation · 1 credit') }}
      </v-btn>
    </v-alert>
  </div>
</template>
<script>

import axios from 'axios'
import { Auth } from 'aws-amplify'
import { t } from '@/i18n'
import { runAi, aiError } from '@/services/ai'
import { categoryPolicy, visibility } from '@/utils/recordPrivacy'

export default {
  props: {
    value: Boolean,
    saved: Boolean,
    disabled: Boolean,
    editing: Boolean,
    recordId: { type: [Number, String], default: null },
    record: { type: Object, required: true },
    categories: { type: Array, default: () => [] },
  },
  data: () => ({
    busy: false, failed: false, message: '', hasTranslation: false, job: null,
  }),
  computed: {
    available() {
      const { user } = this.$store.state

      return visibility(this.record, user && user.extra_data && user.extra_data.public, categoryPolicy(this.categories)).effective_public
    },
  },
  watch: {
    saved(value) { if (value && this.value && this.available) void this.translate({ failedJobId: this.job && this.job.status === 'failed' ? this.job.jobId : null }) },
    available(value) { if (!value) this.$emit('input', false); else if (this.job && ['queued', 'starting', 'processing'].includes(this.job.status)) void this.translate({ jobId: this.job.jobId }) },
    recordId: { immediate: true, handler() { void this.checkStatus() } },
  },
  methods: {
    async checkStatus() {
      if (!this.editing || !this.recordId) return
      try {
        const session = await Auth.currentSession()
        const { data } = await axios.get(`${process.env.VUE_APP_ROOT_API}/ai/translations/${this.recordId}`, { headers: { Authorization: session.getAccessToken().getJwtToken() } })
        this.hasTranslation = data.hasTranslation
        this.job = data.job || null
        if (this.job && ['queued', 'starting', 'processing'].includes(this.job.status)) void this.translate({ jobId: this.job.jobId })
        if (this.job && ['failed', 'review'].includes(this.job.status)) { this.failed = this.job.status === 'failed'; this.message = this.$t(this.job.message) }
      } catch (_) { /* Translation controls do not block record editing. */ }
    },
    async translate(options = {}) {
      if (this.busy || !this.recordId || !this.available) return
      this.busy = true; this.failed = false; this.message = ''
      try {
        const result = await runAi('translate', { record_id: Number(this.recordId), target_locale: 'es-ES' }, options)
        this.hasTranslation = true
        this.message = this.$t(result.credits === 0 ? t('The Spanish version is available. No credits were charged.') : t('Spanish translation published. 1 credit used.'))
      } catch (error) { this.failed = true; this.message = `${this.$t('Your memory is saved. Translation was not completed.')} ${aiError(error)}` } finally { this.busy = false }
    },
  },
}
</script>
